import type {
  DataGridDataRowProps,
  DataGridRef,
  DataGridSettingsMenuItem,
  JsonDataWrapper,
  OdinDataRetrieval,
  OdinDataRetrievalOptions,
  OdinDataSender,
  RowTemplateType,
} from '@myosh/odin-components';
import { DataGrid, DataSearchLocation, type DynamicFormFieldType } from '@myosh/odin-components';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { LinkedUser } from '../../../../@types/users';
import { useHierarchyTypesQuery } from '../../../../redux/services/hierarchy';
import { useLazyGetUsersQuery } from '../../../../redux/services/user';
import { dateFormatBasedOnFilter } from '../../../../utils/data-transformation-utils';
import { processFieldFilters } from '../../../../utils/filter-url-builder-utils';
import { UserFields } from '../../../../utils/user-config';
import { getUsersFieldOptionsData } from '../../../../utils/user-functions';
import { linkedUsersCustomComponents } from '../user-record-link.component';
import { UserRecordLinkFields, usersDataGridColumns } from './user-recordlink-columns-config';

const mapHighlightedRows = (highlightedRows: Array<LinkedUser>) =>
  highlightedRows?.map((item) => {
    return { id: Number(item.value) };
  });

interface AllUsersProps {
  highlightedRows: Array<LinkedUser>;
  onAdded: (value: LinkedUser) => void;
  menuGridSettings?: DataGridSettingsMenuItem[];
}

function AllUsersGrid({ highlightedRows, onAdded, menuGridSettings }: AllUsersProps) {
  const gridRef = useRef<DataGridRef | undefined>(undefined);
  const [getUsers] = useLazyGetUsersQuery();
  const { data: hierarchyTypes } = useHierarchyTypesQuery({ archived: 'false' });
  const gridSubscriber = useRef<OdinDataSender<JsonDataWrapper> | undefined>(undefined);

  useEffect(() => {
    if (highlightedRows.length > 0) {
      gridRef.current?.api.data.addHighlightedRows(mapHighlightedRows(highlightedRows), true);
    } else {
      gridRef.current?.api.data.removeHighlightedRows();
    }
  }, [highlightedRows]);

  const fetchUsersData = useCallback(
    async (subscriber: OdinDataSender<JsonDataWrapper>, options?: OdinDataRetrievalOptions) => {
      if (options?.page && options?.pageSize) {
        try {
          const data = await getUsers({
            fields: UserRecordLinkFields.join(),
            page: options.page,
            pageSize: options.pageSize,
            sortedField: UserFields.lastName,
            sort: options.sortedFields,
            filters: processFieldFilters(options?.fieldFilters, dateFormatBasedOnFilter),
          }).unwrap();

          subscriber.sendData({ data, requestId: options.requestId });
        } catch (_) {
          subscriber.sendData();
        }
      } else {
        subscriber.sendData();
      }
    },
    []
  );

  const gridSettings = useMemo(() => {
    if (hierarchyTypes) {
      return {
        columns: usersDataGridColumns(hierarchyTypes),
        components: linkedUsersCustomComponents,
        fullHeight: true,
        filterLocation: DataSearchLocation.Api,
      };
    }
  }, [hierarchyTypes]);

  const addLinkedUser = (value: LinkedUser) => onAdded(value);

  const rowTemplate: RowTemplateType = (props: DataGridDataRowProps, children: ReactNode | Array<ReactNode>) => (
    <div
      className="flex"
      onClick={() =>
        addLinkedUser({
          value: props.data[UserFields.id] as number,

          text: props.data[UserFields.lastName] + ' ' + props.data[UserFields.firstName],
        })
      }
    >
      {children}
    </div>
  );

  const initializeGridRef = (ref: DataGridRef) => {
    if (!gridRef.current) {
      gridRef.current = ref;

      if (highlightedRows.length > 0) {
        gridRef.current.api.data.addHighlightedRows(mapHighlightedRows(highlightedRows));
      }
    }
  };

  const gridData = useMemo<OdinDataRetrieval>(() => {
    return {
      getData: (subscriber: OdinDataSender<JsonDataWrapper>, options?: OdinDataRetrievalOptions) => {
        if (options?.fieldType === 'COMBOBOX') {
          getUsersFieldOptionsData(subscriber, options);
        } else {
          fetchUsersData(subscriber, options);
        }
      },
      getSubscriber: (subscriber, fieldType?: DynamicFormFieldType) => {
        if (fieldType !== 'COMBOBOX') {
          gridSubscriber.current = subscriber;
        }
      },
    };
  }, []);

  return (
    <div className="flex h-full flex-col gap-4">
      {gridSettings && (
        <DataGrid
          ref={(ref) => {
            if (ref) initializeGridRef(ref);
          }}
          data={gridData}
          gridSettings={gridSettings}
          rowTemplate={rowTemplate}
          customSettingsMenuItems={menuGridSettings}
          showGridMenu={false}
        />
      )}
    </div>
  );
}

export default AllUsersGrid;
